/* oxlint-disable jsx-a11y/no-autofocus */
import { EMAIL } from 'constants/regex'
import { ReactNode, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { routes } from 'types/routes'
import { CustomerEmailForm } from './types'
import { UserTypes } from 'store/auth/types'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { resetMoveFlow, selectUrlSearchParams } from 'store/app/slice.ts'
import { Banner, Button, ButtonLoadingIndicator, Card, Form, List, TextField } from '@boltenergy-be/design-system'
import { getContactByEmail } from 'store/contact/thunks'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import styles from './CustomerEmail.module.scss'
import { logout } from 'utils/app.ts'
import { getUserTypeLoginPage } from 'utils/user.ts'
import { ContactsResponseCodes } from 'types/errorCodes.ts'
import classnames from 'classnames'
import { selectContactByEmail, selectContactEmail } from 'store/contact/slice'
import { UrlSearchParamsKeys } from 'store/app/types'
import { GetContactSuccessPayload } from 'store/contact/thunks/types.ts'
import { DEBUG_ACCOUNT_SELECTION_AS_REGULAR_USER, IS_PRODUCTION } from 'constants/envs.ts'
import { ERROR_CODES } from '@boltenergy-be/shared'

const CustomerEmail = () => {
  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<CustomerEmailForm>()

  // REDUX
  const contactByEmail = useStoreSelector(selectContactByEmail)
  const contactId = useStoreSelector(selectUrlSearchParams)[UrlSearchParamsKeys.CONTACT_ID] || ''
  const contactEmail = useStoreSelector((state) => selectContactEmail(state, contactId))
  const contactEmailLoading = contactEmail?.loading
  const contactEmailError = contactEmail?.error
  const contactByEmailLoading = contactByEmail?.loading
  const contactByEmailError = contactByEmail?.error
  const contactByEmailErrorData = contactByEmail?.errorData
  const hasError = Boolean(contactByEmailError || contactEmailError)
  const dispatch = useStoreDispatch()

  // React Router
  const navigate = useNavigate()

  // i18n
  const { t } = useTranslation(['home', 'common', 'validation'])

  /**
   * Returns the error code as a translated, readable error message for the end users
   *
   * @returns {ReactNode | string}
   */
  const getTranslatedUserError = (): ReactNode | string => {
    if (contactEmailError) {
      return contactEmailError
    }

    // If error data is returned from the API, show it
    if (contactByEmailError === ERROR_CODES.internalServerError && contactByEmailErrorData?.length > 0) {
      return (
        <List as="ol">
          {contactByEmailErrorData.map((error, idx) => (
            <li key={idx}>{error}</li>
          ))}
        </List>
      )
    }

    // "Predefined" error messages
    switch (contactByEmailError) {
      // Contact does not exist or is not a customer
      case ERROR_CODES.notFound:
        return "No Primary Contact with status 'Customer' found in Salesforce for the given e-mail address."

      // Contact has no accounts linked
      case ContactsResponseCodes.NO_ACCOUNTS_FOUND:
        return 'Contact has no accounts linked to it in Salesforce.'

      // Contact has no customer accounts linked in Salesforce
      case ContactsResponseCodes.NO_CUSTOMER_ACCOUNTS_FOUND:
        return 'Contact has no customer accounts (consumer or producer) linked to it in Salesforce.'

      default: {
        const errorToShow =
          typeof contactByEmailError === 'string'
            ? `${contactByEmailError}\nCreate a bug ticket in Notion.`
            : 'Unknown error: create a bug ticket in Notion.'
        return errorToShow
      }
    }
  }

  /**
   * Navigate based on contact data
   * @param {GetContactSuccessPayload | undefined} getContactData
   */
  const simplifiedNavigateBasedOnContactData = (getContactData: GetContactSuccessPayload | undefined) => {
    if (
      getContactData?.selected &&
      getContactData.normalizedStoreData.accounts &&
      getContactData.selected.account in getContactData.normalizedStoreData.accounts
    ) {
      // If single address is available or previously selected from cache, navigate to overview
      void navigate(routes.OVERVIEW)
    } else {
      // Multiple active contracts: redirect to choose address page
      void navigate(routes.ACCOUNT_SELECTION)
    }
  }

  /**
   * Handles the form submit after validation by React Hook Form
   *
   * @param {CustomerEmailForm} data
   */
  const onSubmit = async (data: CustomerEmailForm) => {
    try {
      const { email } = data
      dispatch(resetMoveFlow())
      const contactData = await dispatch(getContactByEmail({ email })).unwrap()

      if (!IS_PRODUCTION && DEBUG_ACCOUNT_SELECTION_AS_REGULAR_USER) {
        // If debugging is enabled & not in production, navigate directly based on contact data
        simplifiedNavigateBasedOnContactData(contactData)
      } else {
        // No error happened so navigate to the account selection
        void navigate(routes.ACCOUNT_SELECTION)
      }
    } catch (error) {
      // The error is already set in the store and handled here so not needed to handle
    }
  }

  // If we set contact_id in the URLSearchParams, set the email we get from it in the input field
  useEffect(() => {
    const emailValue = contactEmail?.data

    if (emailValue) {
      setValue('email', emailValue)
    }
  }, [contactEmail?.data, setValue])

  return (
    <PageLayout
      title={t(($) => $.superUser.welcome)}
      description={t(($) => $.superUser.description[contactEmailLoading ? 'contactId' : 'noContactId'])}
      classes={{ body: styles.body, header: styles.header }}
    >
      <Card>
        {contactEmailLoading ? (
          <div className={styles.spinner}>
            <ButtonLoadingIndicator />
          </div>
        ) : (
          <>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                {...register('email', {
                  required: t(($) => $.required, {
                    ns: 'validation'
                  }),
                  pattern: {
                    value: EMAIL,
                    message: t(($) => $.invalid.email, {
                      ns: 'validation'
                    })
                  },
                  disabled: contactByEmailLoading
                })}
                type="email"
                label={t(($) => $.superUser.email)}
                placeholder={t(($) => $.superUser.email)}
                error={errors?.email?.message}
                grid="full"
                autoFocus
              />

              <Form.Footer>
                <Button loading={contactByEmailLoading}>{t(($) => $.superUser.button)}</Button>
              </Form.Footer>
            </Form>

            {hasError && (
              <Banner className={classnames('mt-500', styles.error)} type="blocking" title="Whoops, there is an issue with the data ⚡️">
                {getTranslatedUserError()}
              </Banner>
            )}
          </>
        )}
      </Card>

      <Button
        onClick={() => logout(() => void navigate(getUserTypeLoginPage(UserTypes.SUPER_USER)), UserTypes.SUPER_USER)}
        className="mt-600"
        variant="tertiary"
      >
        {t(($) => $.nav.logout, {
          ns: 'common'
        })}
      </Button>
    </PageLayout>
  )
}

export default CustomerEmail
